import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Globe, Plus, RefreshCw, Trash2, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = {
  domain: '',
  notes: '',
};

function statusTone(status) {
  if (status === 'active') return 'bg-emerald-100 text-emerald-800';
  if (status === 'verified') return 'bg-sky-100 text-sky-800';
  if (status === 'pending') return 'bg-amber-100 text-amber-800';
  if (status === 'failed') return 'bg-rose-100 text-rose-800';
  if (status === 'disabled') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
}

export default function StoreDomainsPage({ pageTitle = 'Store Domains', pageDescription = 'Connect a custom domain to your Jjuma Store with simple DNS verification.' } = {}) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/dashboard/custom-domains');
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load custom domains');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createDomain = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/dashboard/custom-domains', form);
      toast.success(res.data?.message || 'Custom domain created');
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create domain');
    } finally {
      setSaving(false);
    }
  };

  const verifyDomain = async (domain) => {
    try {
      const res = await api.post(`/dashboard/custom-domains/${domain.id}/verify`, { activate: true });
      toast.success(res.data?.message || 'Domain verified');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify domain');
    }
  };

  const updateDomain = async (domain, payload, successMessage) => {
    try {
      await api.patch(`/dashboard/custom-domains/${domain.id}`, payload);
      toast.success(successMessage);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update domain');
    }
  };

  const deleteDomain = async (domain) => {
    try {
      await api.delete(`/dashboard/custom-domains/${domain.id}`);
      toast.success('Custom domain removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove domain');
    }
  };

  const instructions = data?.dns_instructions || {};
  const domains = data?.domains || [];
  const canCreate = !!data?.can_create;
  const store = data?.store || null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{pageTitle}</h1>
        <p className="text-slate-500 text-sm mt-0.5">{pageDescription}</p>
      </div>

      {!canCreate ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>Your package does not currently allow custom domains.</span>
        </div>
      ) : null}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Globe size={18} /> Add Custom Domain
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={createDomain} className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Custom Domain</Label>
              <Input
                value={form.domain}
                onChange={(e) => setForm(prev => ({ ...prev, domain: e.target.value }))}
                className="mt-1"
                placeholder="example.com or shop.example.com"
                disabled={!canCreate}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-1"
                placeholder="Optional note"
                disabled={!canCreate}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={!canCreate || saving}>
                <Plus size={16} className="mr-1" /> {saving ? 'Saving...' : 'Create Domain'}
              </Button>
              {store?.username ? (
                <p className="text-sm text-slate-500">
                  Your store URL: <span className="font-mono text-slate-900">shop.jjuma.com/{store.username}</span>
                </p>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>DNS Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <p><strong>Root domain:</strong> {instructions.root || 'Create an A record for @ pointing to your server IP.'}</p>
            <p><strong>Subdomain:</strong> {instructions.subdomain || 'Create a CNAME record pointing to shop.jjuma.com.'}</p>
            <p><strong>Verification:</strong> {instructions.verification || 'Add the TXT record with the verification token.'}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Current Domain Mappings</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.length ? domains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium text-slate-900">{domain.domain}</p>
                        <p className="text-xs text-slate-500">Target: {domain.dns_target || 'shop.jjuma.com'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusTone(domain.status)}>{domain.status}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{domain.verification_token || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-600">{domain.created_at ? new Date(domain.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => verifyDomain(domain)} className="border-sky-200 text-sky-700 hover:bg-sky-50">
                        <ShieldCheck size={14} className="mr-1" /> Verify
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => updateDomain(domain, { status: domain.status === 'disabled' ? 'active' : 'disabled' }, domain.status === 'disabled' ? 'Domain enabled' : 'Domain disabled')}>
                        {domain.status === 'disabled' ? 'Enable' : 'Disable'}
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => deleteDomain(domain)} className="border-rose-200 text-rose-700 hover:bg-rose-50">
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-slate-500 py-8">
                      No custom domains added yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
